import { observer } from "mobx-react-lite"
import styles from "./styles.module.scss"
import Edit2Outline from "~icons/eva/edit-2-outline"
import { makeAutoObservable } from "mobx"
import { useEffect, useMemo, useRef } from "react"
import { updateTrip } from "../../../services/firestore/trips"
import { useStores } from "../../../models/helpers/useStores"
import { useMutation } from "@tanstack/react-query"
import classNames from "classnames"
import { formatLastUnreadMessageTime, MINUTE_IN_MS } from "@cruisen/react-shared"
import { TripNavigatorState } from "../../../models/RootStore.ts"
import ArchiveIcon from "../../assets/icons/ArchiveIcon.tsx"
import UnarchiveIcon from "../../assets/icons/UnarchiveIcon.tsx"
import { ModalWrapper } from "../../templates/ModalWrapper/index.tsx"
import { CreateTripFormValues, TripDetailsModal } from "../../organisms/TripDetailsModal/index.tsx"
import { Analytics } from "../../../utils/analytics.ts"
import { OptionPopover } from "../../atoms/OptionPopover/index.tsx"

class TripCardStore {
  activeModal: null | "tripOptions"
  isHoveringCard: boolean
  isTripNameOverflowing: boolean
  isTripDetailsOverflowing: boolean
  navigatorState: TripNavigatorState
  unreadMessageTime: number | null
  formattedUnreadMessageTime: string | null

  constructor(navigatorState: TripNavigatorState) {
    this.activeModal = null
    this.isHoveringCard = false
    this.isTripNameOverflowing = false
    this.isTripDetailsOverflowing = false
    this.formattedUnreadMessageTime = null
    this.setNavigatorState(navigatorState)

    makeAutoObservable(this)
  }

  setNavigatorState(value: TripNavigatorState) {
    this.navigatorState = value

    this.unreadMessageTime =
      Number.isInteger(this.navigatorState.lastMessageIndex) &&
      Number.isInteger(this.navigatorState.readMessageIndex) &&
      this.navigatorState.lastMessageIndex > this.navigatorState.readMessageIndex
        ? this.navigatorState.lastMessageDate
        : null

    this.formattedUnreadMessageTime = formatLastUnreadMessageTime(this.unreadMessageTime)
  }

  setModalVisible(activeModal: null | "tripOptions") {
    this.activeModal = activeModal
  }

  toggleModalVisible(activeModal: null | "tripOptions") {
    this.activeModal = this.activeModal === activeModal ? null : activeModal
  }

  setIsHoveringCard(value: boolean) {
    this.isHoveringCard = value
  }

  setIsTripNameOverflowing(value: boolean) {
    this.isTripNameOverflowing = value
  }

  setIsTripDetailsOverflowing(value: boolean) {
    this.isTripDetailsOverflowing = value
  }

  setFormattedUnreadMessageTime(value: string) {
    this.formattedUnreadMessageTime = value
  }
}

export const TripCard = observer(
  ({
    name,
    tripDetails,
    isSelected,
    id,
    invalidateUserTrips,
    onClick,
    testId,
    areTripDetailsEmpty,
  }: {
    name: string
    tripDetails: string
    isSelected: boolean
    id: string
    invalidateUserTrips: () => void
    onClick: () => void
    testId: string
    areTripDetailsEmpty
  }) => {
    const rootStore = useStores()
    const tripNameRef = useRef(null)
    const tripDetailsRef = useRef(null)

    const navigatorState = rootStore.navigatorStates.get(id) ?? {
      lastMessageDate: 0,
      lastMessageIndex: 0,
      readMessageIndex: 0,
    }

    const store = useMemo(() => new TripCardStore(navigatorState), [])

    const { mutate: mutateArchiveTrip, isPending: isLoading } = useMutation({
      mutationFn: (isArchiving: boolean) => {
        const tags = rootStore.activeTrip.tags.filter((tag) => tag !== "archived")
        return updateTrip(rootStore.activeTrip.id, {
          tags: isArchiving ? [...tags, "archived"] : tags,
        })
      },
      onSuccess: () => {
        store.setModalVisible(null)
        invalidateUserTrips()
      },
    })

    useEffect(() => {
      if (tripNameRef.current) {
        store.setIsTripNameOverflowing(tripNameRef.current.clientWidth > 170)
      }

      if (tripDetailsRef.current) {
        store.setIsTripDetailsOverflowing(tripDetailsRef.current.clientWidth > 170)
      }

      store.setNavigatorState(navigatorState)
    }, [navigatorState?.lastMessageIndex, navigatorState?.readMessageIndex])

    useEffect(() => {
      // Check every minute to update time values
      const interval = setInterval(() => {
        const formattedUnreadMessageTime = formatLastUnreadMessageTime(store.unreadMessageTime)

        store.setFormattedUnreadMessageTime(formattedUnreadMessageTime)
      }, MINUTE_IN_MS)

      return () => clearInterval(interval)
    }, [store.unreadMessageTime])

    const { mutate: mutateUpdateTrip, isPending: isUpdatingTrip } = useMutation({
      mutationFn: async (values: CreateTripFormValues) => {
        await updateTrip(rootStore.activeTrip.id, {
          name: values.tripName.trim(),
          originPortGroups: values.originPortGroups.map((group) => ({
            ...group,
            id: Number(group.id),
          })),
          destinationRegions: values.destinationRegions.map((region) => ({
            ...region,
            id: Number(region.id),
          })),
          cruiseSearchPreferences: null,
          months: values.months.map((e) => e.getTime()),
        })
        Analytics.logUpdateCruiseSearch({
          destinationRegions: values.destinationRegions.map((region) => region.name).join(", "),
          originPortGroups: values.originPortGroups.map((group) => group.name).join(", "),
          months: values.months.map((e) => e.getTime()),
        })
      },
      onSuccess: async () => {
        rootStore.setProp("visibleModal", null)
        invalidateUserTrips()
      },
    })

    const isArchived = rootStore?.activeTrip?.tags?.includes("archived")
    const hasSelectedCruise = Boolean(rootStore?.activeTrip?.chosenTripSearchResultId)

    const options = [
      {
        label: "Edit",
        onClick: () => {
          rootStore.setProp("visibleModal", "editTrip")
          store.setModalVisible(null)
        },
        icon: <Edit2Outline />,
        isHidden: hasSelectedCruise,
      },
      {
        label: isLoading ? "Loading..." : isArchived ? "Restore" : "Archive",
        onClick: () => mutateArchiveTrip(!isArchived),
        icon: isArchived ? <UnarchiveIcon /> : <ArchiveIcon />,
      },
    ]

    return (
      <>
        <button
          onClick={onClick}
          onMouseEnter={() => store.setIsHoveringCard(true)}
          onMouseLeave={() => store.setIsHoveringCard(false)}
          className={classNames(
            styles.tripCardContainer,
            isSelected ? styles.isSelected : styles.isNotSelected,
          )}
          data-testid={testId}
        >
          <div className={styles.tripCardRow}>
            <div className={styles.stack}>
              <p
                className={classNames(
                  styles.tripName,
                  store.isTripNameOverflowing ? styles.animateOverflow : "",
                )}
                ref={tripNameRef}
              >
                {name}
              </p>
              <p
                className={classNames(
                  styles.tripDetails,
                  store.isTripDetailsOverflowing ? styles.animateOverflow : "",
                )}
                ref={tripDetailsRef}
              >
                {areTripDetailsEmpty ? "—" : tripDetails}
              </p>
              <div className={styles.overlay}></div>
            </div>
            {isSelected || store.isHoveringCard ? (
              <OptionPopover
                options={options}
                isOpen={store.activeModal === "tripOptions"}
                onClose={() => store.setModalVisible(null)}
                testId={testId}
                openPopover={() => store.toggleModalVisible(store.activeModal || "tripOptions")}
                wrapperClassName={styles.tripOptionsPopoverWrapper}
                moreButtonClassName={isSelected ? styles.editTripButton : ""}
                align="end"
              />
            ) : store.formattedUnreadMessageTime ? (
              <div className={styles.unreadMessageDateBadge}>
                <p>{store.formattedUnreadMessageTime}</p>
              </div>
            ) : null}
          </div>
        </button>
        {rootStore.visibleModal === "editTrip" && rootStore.activeTrip?.id === id ? (
          <ModalWrapper>
            <TripDetailsModal
              closeModal={() => rootStore.setProp("visibleModal", null)}
              defaultValues={{
                tripName: rootStore.activeTrip.name,
                originPortGroups: rootStore.activeTrip.originPortGroups,
                destinationRegions: rootStore.activeTrip.destinationRegions,
                months: rootStore.activeTrip.months.map((month: number) => new Date(month)),
              }}
              onSubmit={(values) => mutateUpdateTrip(values)}
              isLoading={isUpdatingTrip}
            />
          </ModalWrapper>
        ) : null}
      </>
    )
  },
)
